import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import {
  useIsAuthenticated,
  useMsal,
  useMsalAuthentication,
} from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiScopes } from './msal';

import { apiClient } from '@/api/axios';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { configi18next } from '@/locales';
import { decodeJWT } from '@/utils/auth';
import { Loader } from '@/apps/dispatcher/components/blocks/loader/Loader';

configi18next();

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const { t } = useTranslation();
  const { result, error } = useMsalAuthentication(InteractionType.Silent, {
    scopes: apiScopes,
    account: accounts.length > 0 ? accounts[0] : undefined,
    redirectUri: window.location.origin,
  });

  useTokenRefresh();

  const validateToken = (token: string) => {
    try {
      const decodedToken = decodeJWT(token);
      if (!decodedToken?.exp) {
        return false;
      }
      const expiryTime = decodedToken.exp * 1000;
      const currentTime = Date.now();
      return expiryTime > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  };

  useEffect(() => {
    if (result) {
      const token = result.accessToken;
      apiClient.defaults.headers.Authorization = `Bearer ${token}`;
      instance.setActiveAccount(result.account);
      setIsTokenValid(validateToken(token));
      setCheckingAuth(false);
    } else if (error) {
      console.log('Silent auth error, will try redirect:', error);
      setIsTokenValid(false);
      setCheckingAuth(false);
    }
  }, [result, error, instance]);

  useEffect(() => {
    const handleRedirectLogin = async () => {
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      const activeAccount = instance.getActiveAccount();

      if (!activeAccount && !checkingAuth) {
        try {
          await instance.loginRedirect({
            scopes: apiScopes,
            redirectUri: window.location.origin,
          });
          return;
        } catch (err) {
          console.error('Login redirect error:', err);
          setCheckingAuth(false);
          return;
        }
      }

      if (activeAccount) {
        try {
          const silentResult = await instance.acquireTokenSilent({
            scopes: apiScopes,
            account: activeAccount,
          });

          if (silentResult) {
            apiClient.defaults.headers.Authorization = `Bearer ${silentResult.accessToken}`;
            setIsTokenValid(validateToken(silentResult.accessToken));
            setCheckingAuth(false);
          }
        } catch (silentErr) {
          console.warn('Silent token acquisition failed, initiating redirect');
          await instance.loginRedirect({
            scopes: apiScopes,
            redirectUri: window.location.origin,
          });
        }
      }
    };

    handleRedirectLogin();
  }, [
    isAuthenticated,
    checkingAuth,
    inProgress,
    accounts,
    error,
    instance,
    isTokenValid,
  ]);

  if (checkingAuth || inProgress !== InteractionStatus.None) {
    return <Loader text={t('utils.loader.checking_auth')} />;
  }

  if (!isAuthenticated || !isTokenValid) {
    return <Loader text={t('utils.loader.redirecting')} />;
  }

  return <>{children}</>;
};

export default AuthProvider;
